import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Package, MessageSquare, ShoppingBag, TrendingUp, Bell, Inbox, Coins, Wrench, Sprout, ArrowUpRight } from "lucide-react";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import WeatherPanel from "@/components/WeatherPanel";
import { DASH } from "@/constants/testIds";

const iconMap = { farmer: Sprout, day_worker: Wrench, investor: Coins };

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [stats, setStats] = useState({});
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const HeaderIcon = iconMap[user?.role] || Sprout;

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const greet = (() => {
    const h = new Date().getHours();
    if (h < 12) return t("dash.morning");
    if (h < 17) return t("dash.afternoon");
    return t("dash.evening");
  })();

  const actionRoute = {
    post_product: "/post",
    post_service: "/post",
    orders: "/orders",
    responses: "/responses",
    messages: "/messages",
    marketplace: "/marketplace",
  };

  return (
    <AppShell>
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 h-72">
          <img src={theme.hero} alt="" className="w-full h-full object-cover" />
          <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient} opacity-90`} />
        </div>
        <div className="relative px-4 md:px-8 lg:px-12 pt-10 pb-16 text-white">
          <div className="text-xs tracking-[0.24em] uppercase opacity-90">{greet}</div>
          <div className="mt-2 flex items-center gap-4 flex-wrap">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">{t("dash.welcome")} <span className="text-white">{user?.full_name?.split(" ")[0]}</span>.</h1>
            <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/25 text-sm backdrop-blur-xl`}>
              <HeaderIcon className="h-4 w-4" /> {theme.name} {t("dash.workspace")}
            </div>
          </div>
          <p className="mt-2 text-white/80 max-w-xl">Everything you need to grow — curated for your role, updated in real-time.</p>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12 -mt-10 pb-16">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {theme.dashCards.map((c, i) => {
            const route = c.key === "listings_count" ? "/marketplace?mine=1"
              : c.key === "orders_received" ? "/orders?tab=seller"
              : c.key === "orders_placed" ? "/orders?tab=buyer"
              : c.key === "products_sold" ? "/orders?tab=seller"
              : c.key === "responses" ? "/responses"
              : c.key === "revenue" ? "/orders?tab=seller" : "/dashboard";
            return (
              <motion.button key={c.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                data-testid={`stat-${c.key}`} onClick={() => nav(route)}
                className="text-left rounded-3xl bg-white p-6 premium-shadow border border-gray-100 hover:-translate-y-1 transition-all">
                <div className="flex items-start justify-between">
                  <div className={`h-10 w-10 rounded-xl ${theme.bgSoft} flex items-center justify-center ${theme.text}`}>
                    <StatIcon k={c.key} />
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-gray-400" />
                </div>
                <div className="mt-4 text-3xl font-bold text-gray-900 tracking-tight">
                  {c.isCurrency ? "₹" : ""}{stats[c.key] ?? 0}
                </div>
                <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">{c.label}</div>
              </motion.button>
            );
          })}
        </div>

        {/* Quick actions */}
        <div className="mt-10">
          <div className="flex items-baseline justify-between">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{t("dash.quick_actions")}</h2>
            <div className="text-xs uppercase tracking-wider text-gray-500">Role: {theme.name}</div>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {theme.actions.map((a) => {
              const tid = a.key === "post_product" ? DASH.postProduct : a.key === "post_service" ? DASH.postService : `dash-action-${a.key}`;
              return (
                <button key={a.key} data-testid={tid} onClick={() => nav(actionRoute[a.key] || "/dashboard")}
                  className={`group relative overflow-hidden rounded-2xl border ${theme.border} bg-white p-5 text-left hover:-translate-y-1 transition-all premium-shadow`}>
                  <div className={`h-9 w-9 rounded-xl ${theme.bg} text-white flex items-center justify-center mb-3`}>
                    <ActionIcon k={a.key} />
                  </div>
                  <div className="font-semibold text-gray-900">{a.label}</div>
                  <div className="text-xs text-gray-500 mt-1">Open</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Categories preview */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-3xl bg-white border border-gray-100 p-8 premium-shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500">{t("dash.your_marketplace")}</div>
                <h3 className="mt-1 text-2xl font-bold text-gray-900">{t("dash.categories_curated")} {theme.name}s</h3>
              </div>
              <button data-testid="dash-view-all-categories" onClick={() => nav("/marketplace")} className={`text-sm font-semibold ${theme.text} hover:underline`}>{t("dash.browse_all")}</button>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {theme.categories.map((c) => (
                <button key={c} onClick={() => nav(`/marketplace?category=${encodeURIComponent(c)}`)}
                  className={`px-4 py-2 rounded-full text-sm ${theme.bgSoft} ${theme.text} border ${theme.border} hover:opacity-80`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          {user?.role === "farmer" && <WeatherPanel />}
        </div>

        {/* Community Issues entry — farmer + day_worker only */}
        {user && ["farmer", "day_worker"].includes(user.role) && (
          <div className="mt-10 rounded-3xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-8 premium-shadow overflow-hidden relative">
            <div className="absolute right-6 top-6 text-6xl opacity-20 select-none">🌱</div>
            <div className="text-xs uppercase tracking-[0.24em] opacity-80">Community</div>
            <h3 className="mt-1 text-2xl md:text-3xl font-bold tracking-tight">🌱 Community Issues</h3>
            <p className="mt-2 text-white/90 max-w-xl">See a local problem? Report it and make it visible. Water, roads, electricity, drainage — bring your village voice to GTF.</p>
            <div className="mt-5 flex gap-3 flex-wrap">
              <button data-testid="dash-community-report" onClick={() => nav("/community/report")}
                className="px-5 py-3 rounded-full bg-white text-emerald-700 font-semibold">+ Report Local Issue</button>
              <button data-testid="dash-community-view" onClick={() => nav("/community")}
                className="px-5 py-3 rounded-full bg-white/15 border border-white/30 font-semibold">View all issues</button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function StatIcon({ k }) {
  const map = { listings_count: Package, orders_received: ShoppingBag, orders_placed: ShoppingBag, products_sold: TrendingUp, revenue: TrendingUp, responses: Inbox, unread_notifications: Bell };
  const I = map[k] || Package;
  return <I className="h-5 w-5" />;
}
function ActionIcon({ k }) {
  const map = { post_product: Plus, post_service: Plus, orders: ShoppingBag, responses: Inbox, messages: MessageSquare, marketplace: Package };
  const I = map[k] || Plus;
  return <I className="h-4 w-4" />;
}
